import React, { useEffect, useRef } from "react";
import { CaretDownIcon, CheckIcon, PlusIcon } from "@phosphor-icons/react";
import { Platform, Pressable, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import type { CourseTermOption } from "./courseTermOptions";

type CourseTermSelectVariant = "row" | "modal";

type CourseTermSelectProps = {
  value: string | null | undefined;
  options: CourseTermOption[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (value: string) => void;
  onClear?: () => void;
  variant: CourseTermSelectVariant;
  placeholder?: string;
  accessibilityLabel?: string;
  containerStyle?: StyleProp<ViewStyle>;
};

function CourseTermSelect({
  value,
  options,
  open,
  onOpenChange,
  onChange,
  onClear,
  variant,
  placeholder = "Select term...",
  accessibilityLabel = "Course term",
  containerStyle,
}: CourseTermSelectProps) {
  const containerRef = useRef<View | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel = selectedOption?.label ?? value ?? null;
  const displayLabel = selectedLabel ?? (variant === "row" ? "Set term" : placeholder);
  const hasValue = value != null;
  const isUnsetRowTerm = variant === "row" && !hasValue;
  const triggerTextSize = variant === "row" ? "xs" : "s";
  const canClearTerm = Boolean(hasValue && onClear);

  useEffect(() => {
    if (!open || Platform.OS !== "web") {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const root = containerRef.current as unknown as HTMLElement | null;
      if (root && typeof root.contains === "function" && !root.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onOpenChange, open]);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    onOpenChange(false);
  };

  const handleClear = () => {
    onClear?.();
    onOpenChange(false);
  };

  return (
    <View
      ref={containerRef}
      style={[
        styles.container,
        variant === "modal" ? styles.modalContainer : styles.rowContainer,
        open && styles.containerOpen,
        containerStyle,
      ]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={selectedLabel ? `${accessibilityLabel}: ${selectedLabel}` : accessibilityLabel}
        role="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onPress={() => onOpenChange(!open)}
        style={[
          styles.trigger,
          variant === "modal" ? styles.modalTrigger : styles.rowTrigger,
          isUnsetRowTerm && styles.unsetRowTrigger,
          open && variant === "modal" && styles.modalTriggerOpen,
        ]}>
        {isUnsetRowTerm ? <PlusIcon size={12} color="#C2410C" weight="bold" /> : null}
        <CustomText
          text={displayLabel}
          size={triggerTextSize}
          weight={selectedLabel || variant === "modal" ? "medium" : "regular"}
          useTranslationText={false}
          style={[
            styles.triggerText,
            variant === "row" && styles.rowTriggerText,
            variant === "modal" && !selectedLabel && styles.placeholderText,
            isUnsetRowTerm && styles.unsetRowTriggerText,
          ]}
        />
        {variant === "modal" ? <CaretDownIcon size={16} color="#98A2B3" weight="bold" /> : null}
      </Pressable>

      {open ? (
        <View
          role="menu"
          accessibilityLabel={accessibilityLabel}
          style={[styles.menu, variant === "modal" ? styles.modalMenu : styles.rowMenu]}>
          {options.map((option) => (
            <Pressable
              key={option.value}
              accessibilityRole="menuitem"
              accessibilityLabel={option.label}
              role="menuitem"
              onPress={() => handleSelect(option.value)}
              style={styles.menuItem}>
              <CustomText text={option.label} size="s" useTranslationText={false} style={styles.menuItemText} />
              {option.value === selectedOption?.value ? (
                <CheckIcon size={16} color="#3E68FE" weight="bold" />
              ) : (
                <View style={styles.menuItemIconSlot} />
              )}
            </Pressable>
          ))}
          {canClearTerm ? (
            <>
              <View style={styles.menuDivider} />
              <Pressable
                accessibilityRole="menuitem"
                accessibilityLabel="Clear term"
                role="menuitem"
                onPress={handleClear}
                style={[styles.menuItem, styles.clearMenuItem]}>
                <CustomText
                  text="Clear term"
                  size="s"
                  useTranslationText={false}
                  style={[styles.menuItemText, styles.clearMenuItemText]}
                />
                <View style={styles.menuItemIconSlot} />
              </Pressable>
            </>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
    overflow: "visible",
    zIndex: 1,
  },
  rowContainer: {
    alignSelf: "flex-start",
  },
  modalContainer: {
    width: 180,
  },
  containerOpen: {
    zIndex: 100000,
    elevation: 100000,
  },
  trigger: {
    flexDirection: "row",
    alignItems: "center",
  },
  rowTrigger: {
    minHeight: 28,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "transparent",
    backgroundColor: "transparent",
  },
  unsetRowTrigger: {
    gap: 5,
    borderColor: "#FDE68A",
    borderStyle: "dashed",
    backgroundColor: "#FFFBEB",
  },
  modalTrigger: {
    minHeight: 36,
    justifyContent: "space-between",
    gap: 12,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: "#FFFFFF",
  },
  modalTriggerOpen: {
    borderColor: "#6685FF",
    shadowColor: "#3E68FE",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 0,
  },
  triggerText: {
    color: "#344054",
  },
  rowTriggerText: {
    fontSize: 13,
    lineHeight: 18,
  },
  placeholderText: {
    color: "#344054",
  },
  unsetRowTriggerText: {
    color: "#C2410C",
  },
  menu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    shadowColor: "#101828",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    zIndex: 100000,
    elevation: 100000,
    paddingVertical: 6,
  },
  rowMenu: {
    width: 170,
  },
  modalMenu: {
    width: "100%",
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  menuItemText: {
    flex: 1,
    color: "#101828",
    fontSize: 14,
    lineHeight: 20,
  },
  menuItemIconSlot: {
    width: 16,
    height: 16,
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#EAECF0",
    marginVertical: 4,
  },
  clearMenuItem: {
    paddingTop: 8,
  },
  clearMenuItemText: {
    color: "#344054",
  },
});

export default CourseTermSelect;
